import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import toast from 'react-hot-toast';
import type {
  WhatsappConversation,
  WhatsappMessage,
  WhatsappAgentConfig,
  InboxStats,
  ConversationStatus,
} from '@/types/outreach';

// --- Conversations ---

interface ConversationsResponse {
  count: number;
  results: WhatsappConversation[];
}

export const useConversations = (search?: string, status?: ConversationStatus | '', interested?: string, starred?: boolean) => {
  return useInfiniteQuery({
    queryKey: ['whatsapp-conversations', search, status, interested, starred],
    queryFn: async ({ pageParam = 1 }) => {
      const params: Record<string, string> = { page: String(pageParam), page_size: '20' };
      if (search) params.search = search;
      if (status) params.status = status;
      if (interested) params.interested = interested;
      if (starred !== undefined) params.starred = String(starred);
      const { data } = await api.get<ConversationsResponse>(API_ROUTES.whatsappConversations, {
        params,
      });
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.results.length, 0);
      if (loaded >= lastPage.count) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1,
    refetchInterval: 5000,
  });
};

export const useConversationDetail = (id: string | null) => {
  return useQuery({
    queryKey: ['whatsapp-conversation', id],
    queryFn: async () => {
      const { data } = await api.get<WhatsappConversation>(
        `${API_ROUTES.whatsappConversations}${id}/`,
      );
      return data;
    },
    enabled: !!id,
  });
};

interface MessagesResponse {
  count: number;
  results: WhatsappMessage[];
}

export const useConversationMessages = (id: string | null, after?: string) => {
  return useQuery({
    queryKey: ['whatsapp-messages', id, after],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (after) params.after = after;
      const { data } = await api.get<MessagesResponse>(
        `${API_ROUTES.whatsappConversations}${id}/messages/`,
        { params },
      );
      return data;
    },
    enabled: !!id,
    refetchInterval: 2500,
  });
};

// --- Mutations ---

export const useSendAdminMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      content,
    }: {
      conversationId: string;
      content: string;
    }) => {
      const { data } = await api.post<WhatsappMessage>(
        `${API_ROUTES.whatsappConversations}${conversationId}/send/`,
        { content },
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-messages', variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });
};

export const useUpdateConversationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      status,
    }: {
      conversationId: string;
      status: ConversationStatus;
    }) => {
      const { data } = await api.patch(
        `${API_ROUTES.whatsappConversations}${conversationId}/status/`,
        { status },
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-conversation', variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-inbox-stats'] });
    },
  });
};

export const useToggleInterest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data } = await api.post(`${API_ROUTES.whatsappConversations}${conversationId}/interest/`);
      return data as { isInterested: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });
};

export const useMarkConversationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data } = await api.post(`${API_ROUTES.whatsappConversations}${conversationId}/read/`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-inbox-stats'] });
    },
  });
};

// --- Inbox Stats ---

export const useInboxStats = () => {
  return useQuery({
    queryKey: ['whatsapp-inbox-stats'],
    queryFn: async () => {
      const { data } = await api.get<InboxStats>(API_ROUTES.whatsappInboxStats);
      return data;
    },
    refetchInterval: 10000,
  });
};

// --- Agent Config ---

export const useAgentConfigs = () => {
  return useQuery({
    queryKey: ['whatsapp-agent-configs'],
    queryFn: async () => {
      const { data } = await api.get<WhatsappAgentConfig[]>(API_ROUTES.whatsappAgentConfig);
      return data;
    },
  });
};

export const useSaveAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      config: Partial<WhatsappAgentConfig> & { name: string; systemPrompt: string },
    ) => {
      if (config.id) {
        const { data } = await api.put<WhatsappAgentConfig>(
          `${API_ROUTES.whatsappAgentConfig}${config.id}/`,
          config,
        );
        return data;
      }
      const { data } = await api.post<WhatsappAgentConfig>(API_ROUTES.whatsappAgentConfig, config);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-agent-configs'] });
    },
  });
};

export const useDeleteAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.whatsappAgentConfig}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-agent-configs'] });
    },
  });
};

// --- Star ---

interface StarResponse {
  id: string;
  isStarred: boolean;
  starredAt: string | null;
}

interface ToggleStarArgs {
  conversationId: string;
  isStarred: boolean;
}

export const useToggleStar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, isStarred }: ToggleStarArgs): Promise<StarResponse> => {
      const { data } = await api.patch<StarResponse>(
        `${API_ROUTES.whatsappConversations}${conversationId}/star/`,
        { isStarred },
      );
      return data;
    },
    onMutate: async ({ conversationId, isStarred }) => {
      // Cancel any outgoing queries
      await queryClient.cancelQueries({ queryKey: ['whatsapp-conversations'] });
      await queryClient.cancelQueries({ queryKey: ['whatsapp-conversation', conversationId] });

      // Snapshot for rollback
      const prevList = queryClient.getQueriesData<{
        pages: { count: number; results: WhatsappConversation[] }[];
        pageParams: unknown[];
      }>({ queryKey: ['whatsapp-conversations'] });
      const prevDetail = queryClient.getQueryData<WhatsappConversation>(['whatsapp-conversation', conversationId]);

      // Optimistically update list queries
      queryClient.setQueriesData<{
        pages: { count: number; results: WhatsappConversation[] }[];
        pageParams: unknown[];
      }>({ queryKey: ['whatsapp-conversations'] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: page.results.map((c) =>
              c.id === conversationId
                ? { ...c, isStarred, starredAt: isStarred ? new Date().toISOString() : null }
                : c,
            ),
          })),
        };
      });

      // Optimistically update detail query
      if (prevDetail) {
        queryClient.setQueryData<WhatsappConversation>(['whatsapp-conversation', conversationId], {
          ...prevDetail,
          isStarred,
          starredAt: isStarred ? new Date().toISOString() : null,
        });
      }

      return { prevList, prevDetail };
    },
    onError: (_err, { conversationId }, context) => {
      // Rollback list
      if (context?.prevList) {
        for (const [queryKey, data] of context.prevList) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      // Rollback detail
      if (context?.prevDetail) {
        queryClient.setQueryData(['whatsapp-conversation', conversationId], context.prevDetail);
      }
      toast.error('Could not update star. Please try again.', { duration: 8000 });
    },
    onSettled: (_data, _err, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversation', conversationId] });
    },
  });
};
